import {Component, ViewChild, Renderer2} from '@angular/core';
import {SystemGlobalsService} from './system-globals.service';
import { environment } from '../environments/environment';

declare var window: any;
declare var appGlobals: any;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})

export class AppComponent {
  @ViewChild('sidenav', {static: false}) sidenav;

  title = 'App';
  public showTopMenu: boolean = true;
  public showSideNav: boolean = true;
  public showAiChat: boolean = false; // Hidden by default for production rollout
  private hideLayoutUrls: string[] = [
    '/main/communications/memos/read',
    '/main/documents/media',
    'hideContent',
    '/main/ai-chat',
    '/main/records/competenciesframe',
    '/main/auth/new-login'
  ];
  private scriptsLoaded: boolean = false;

  constructor(public globals: SystemGlobalsService, private renderer: Renderer2)
  {
    // Hide layout if we are on certain pages
    let hideLayout = this.hideLayoutUrls.some(url => window.location.href.includes(url));

    if (hideLayout)
    {
      this.showSideNav = false;
      this.showTopMenu = false;
      this.globals.showSystemBannerNotice = false;
    }

    // AI Chat visibility control for production rollout
    // Hidden by default, can be enabled with URL parameter: ?enableAiChat=true
    // Or by adding ?showAiChat=1 to the URL
    this.checkAiChatVisibility();

    // Load scripts if showTopMenu is true
    if (this.showTopMenu)
    {
      this.loadExternalScripts();
    }
  }

  private checkAiChatVisibility() {
    const urlParams = new URLSearchParams(window.location.search);
    const enableAiChat = urlParams.get('enableAiChat') === 'true' || urlParams.get('showAiChat') === '1';

    // Enable for specific test users (add your user ID here)
    // const testUserIds = [23]; // Add more user IDs as needed
    // const isTestUser = testUserIds.includes(this.globals.constants._g_userID);
    const isTestUser = false;

    // Enable for development environment
    // const isDevelopment = window.location.hostname.includes('dev') || window.location.hostname.includes('localhost');
    const isDevelopment = false;

    this.showAiChat = enableAiChat || isTestUser || isDevelopment;

    if (this.showAiChat && !enableAiChat)
    {
      console.log('AI Chat enabled for test user or development environment');
    }
  }

  hideSystemBanner()
  {
    this.globals.hideSystemBanner();
  }

  handlemobileNavToggle(event)
  {
    this.sidenav.toggleMobileNav();
  }

  private loadExternalScripts()
  {
    if (this.scriptsLoaded)
    {
      return;
    }

    this.scriptsLoaded = true;

    this.loadGoogleAnalytics();
    // Load HelpScout Beacon
    this.loadHelpScoutBeacon();

    // Load Sentry
    this.loadSentry();
  }

  private loadGoogleAnalytics()
  {
    const gtagId = this.resolveGtagId();

    if (!gtagId)
    {
      return;
    }

    const gaScript = this.renderer.createElement('script');
    gaScript.async = true;
    gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${gtagId}`;
    this.renderer.appendChild(document.body, gaScript);

    const gaInit = this.renderer.createElement('script');
    gaInit.type = 'text/javascript';
    gaInit.text = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${gtagId}');
    `;

    this.renderer.appendChild(document.body, gaInit);
  }

  private resolveGtagId(): string | undefined
  {
    const gtagConfig: any = environment.gtag;

    if (!gtagConfig)
    {
      return undefined;
    }

    if (typeof gtagConfig === 'string')
    {
      return gtagConfig;
    }

    const hostname = (window.location.hostname || '').toLowerCase();
    const isAscent = hostname.includes('aerostudies') || hostname.includes('ascent');
    const isAvanti = hostname.includes('allantra') || hostname.includes('avanti');

    if (isAscent && gtagConfig.ascent)
    {
      return gtagConfig.ascent;
    }

    if (isAvanti && gtagConfig.avanti)
    {
      return gtagConfig.avanti;
    }

    return gtagConfig.default || gtagConfig.ascent || gtagConfig.avanti;
  }

  private loadHelpScoutBeacon()
  {
    // HelpScout Beacon initialization
    const beaconInit = this.renderer.createElement('script');
    beaconInit.type = 'text/javascript';
    beaconInit.text = `
      !function(e,t,n){function a(){var e=t.getElementsByTagName("script")[0],n=t.createElement("script");n.type="text/javascript",n.async=!0,n.src="https://beacon-v2.helpscout.net",e.parentNode.insertBefore(n,e)}if(e.Beacon=n=function(t,n,a){e.Beacon.readyQueue.push({method:t,options:n,data:a})},n.readyQueue=[],"complete"===t.readyState)return a();e.attachEvent?e.attachEvent("onload",a):e.addEventListener("load",a,!1)}(window,document,window.Beacon||function(){});
    `;
    this.renderer.appendChild(document.body, beaconInit);

    // HelpScout Beacon config
    const beaconConfig = this.renderer.createElement('script');
    beaconConfig.type = 'text/javascript';
    beaconConfig.text = `
      if (typeof appGlobals !== 'undefined' && appGlobals.constants.userID && appGlobals.constants.userRole != 2) {
        window.Beacon('init', appGlobals.constants.helpScoutKey);
      }
      if (typeof window.Beacon !== 'undefined') {
        window.Beacon('config', {'display': {'zIndex': 999}});
      }
    `;
    this.renderer.appendChild(document.body, beaconConfig);
  }

  private loadSentry()
  {
    // Load Sentry SDK
    const sentryScript = this.renderer.createElement('script');
    sentryScript.src = 'https://js.sentry-cdn.com/aa714091ce38f4f291e8aa13225982f5.min.js';
    sentryScript.crossOrigin = 'anonymous';
    this.renderer.appendChild(document.body, sentryScript);

    // Sentry initialization
    const sentryInit = this.renderer.createElement('script');
    sentryInit.type = 'text/javascript';
    sentryInit.text = `
      window.sentryOnLoad = function () {
        Sentry.init({
          dsn: "https://aa714091ce38f4f291e8aa13225982f5@o4509797413683200.ingest.us.sentry.io/4509797416108032",
          sendDefaultPii: false,
          release: "ascent@1.0.0",
          integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration(),
          ],
          enableLogs: true,
          tracesSampleRate: 0.2,
          tracePropagationTargets: [
            "localhost",
            /^https:\\/\\/aerostudies\\.com/,
          ],
          replaysSessionSampleRate: 0.1,
          replaysOnErrorSampleRate: 1.0,
        });
      };
    `;
    this.renderer.appendChild(document.body, sentryInit);
  }
}
